import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';

const UNIT_REFERENCE = [
  { label: 'EMS Station', type: 'station', table: 'ems_station' },
  { label: 'EMS Station Expired', type: 'station', table: 'ems_station_expired' },
  { label: 'Mayo 21', type: 'ambulance', table: 'mayo_21' },
  { label: 'Mayo 22', type: 'ambulance', table: 'mayo_22' },
  { label: 'Mayo 23', type: 'ambulance', table: 'mayo_23' },
  { label: 'Mayo 24', type: 'ambulance', table: 'mayo_24' },
  { label: 'Mayo 25', type: 'ambulance', table: 'mayo_25' },
];

const MED_REFERENCE = [
  'Fentanyl', 'Hydromorphone', 'Ketamine', 'Lorazepam', 'Propofol',
  'Rocuronium', 'Etomidate', 'Midazolam 10mg/2mL', 'Midazolam 5mg/5mL',
  'Midazolam 2mg/2mL', 'Diltiazem',
];

const DEFAULTS = {
  daily: {
    system_role: "You are an AI compliance analyst for Mayo Clinic's controlled substance inventory management system (NarcoticTrack). Your role is to analyze the last 24 hours of narcotic inventory logs across all units and generate a concise, actionable compliance report. You focus on identifying discrepancies, missing logs, policy violations, and patterns that require supervisor attention. You are direct, precise, and prioritize patient safety and DEA compliance.",
    flag_criteria: `NARCOTIC FLAG CRITERIA:
- CRITICAL: Any unit with zero logs in 24h when the unit should be active, any medication quantity discrepancy between consecutive records without a documented reason (such as Patient Use/Restock or Expiration/Restock), any missing or null data in required fields, any quantity going negative.
- WARNING: Seal number changes without corresponding documentation, units with only 1 log in 24h, medication quantities reaching zero, same personnel logging all entries for a unit (no cross-verification).
- INFO: Notes from personnel worth highlighting, run numbers logged (indicating medication use calls), routine seal verifications, new personnel appearing in logs.`,
    analysis_priorities: '',
    additional_instructions: '',
  },
  weekly: {
    system_role: "You are an AI compliance strategist for Mayo Clinic, generating a WEEKLY intelligence report for narcotic inventory and controlled substance management. Think like a Division Chief reviewing a week of operations. Focus on PATTERNS and TRENDS across the entire week, not individual daily incidents. Identify systemic issues, personnel patterns, compliance trajectory, and strategic recommendations. Your audience is senior leadership.",
    flag_criteria: `NARCOTIC FLAG CRITERIA:
- CRITICAL: Any unit with zero logs in 24h when the unit should be active, any medication quantity discrepancy between consecutive records without a documented reason (such as Patient Use/Restock or Expiration/Restock), any missing or null data in required fields, any quantity going negative.
- WARNING: Seal number changes without corresponding documentation, units with only 1 log in 24h, medication quantities reaching zero, same personnel logging all entries for a unit (no cross-verification).
- INFO: Notes from personnel worth highlighting, run numbers logged (indicating medication use calls), routine seal verifications, new personnel appearing in logs.`,
    analysis_priorities: `ANALYSIS PRIORITIES:
1. Compliance trends: Is overall compliance improving, stable, or declining week-over-week?
2. Medication usage patterns: Any unusual consumption rates, unexpected zero-quantity events, or restock frequency anomalies?
3. Personnel accountability: Are all expected personnel logging? Any gaps in coverage? Cross-verification patterns?
4. Systemic issues: Recurring problems that suggest process or training gaps rather than one-off incidents.
5. Comparison to daily flags: Were critical flags from daily reports addressed? Any that persisted all week?`,
    additional_instructions: '',
  },
};

function PromptTemplateEditor({ userInfo }) {
  const [activeTab, setActiveTab] = useState('daily');
  const [template, setTemplate] = useState(null);
  const [savedValues, setSavedValues] = useState(null);
  const [fields, setFields] = useState({
    system_role: '',
    flag_criteria: '',
    analysis_priorities: '',
    additional_instructions: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    loadTemplate(activeTab);
  }, [activeTab]);

  const loadTemplate = async (tab) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('ai_prompt_templates')
        .select('*')
        .eq('prompt_type', tab)
        .eq('deployment_id', 'mayo')
        .eq('is_active', true)
        .limit(1);

      if (error) {
        console.error('Error loading template:', error);
        return;
      }

      const t = data && data.length > 0 ? data[0] : null;
      setTemplate(t);

      const vals = {
        system_role: t?.system_role || DEFAULTS[tab].system_role,
        flag_criteria: t?.flag_criteria || DEFAULTS[tab].flag_criteria,
        analysis_priorities: t?.analysis_priorities || DEFAULTS[tab].analysis_priorities,
        additional_instructions: t?.additional_instructions || DEFAULTS[tab].additional_instructions,
      };
      setFields(vals);
      setSavedValues({ ...vals });
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = savedValues && (
    fields.system_role !== savedValues.system_role ||
    fields.flag_criteria !== savedValues.flag_criteria ||
    fields.analysis_priorities !== savedValues.analysis_priorities ||
    fields.additional_instructions !== savedValues.additional_instructions
  );

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        prompt_type: activeTab,
        deployment_id: 'mayo',
        name: 'Default',
        system_role: fields.system_role,
        flag_criteria: fields.flag_criteria,
        analysis_priorities: fields.analysis_priorities || null,
        additional_instructions: fields.additional_instructions || '',
        is_active: true,
        updated_by: userInfo?.id || null,
      };

      if (template?.id) {
        await supabase
          .from('ai_prompt_templates')
          .update(payload)
          .eq('id', template.id);
      } else {
        await supabase
          .from('ai_prompt_templates')
          .insert(payload);
      }

      setSavedValues({ ...fields });
    } catch (err) {
      console.error('Error saving:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const defaults = DEFAULTS[activeTab];
    setFields({
      system_role: defaults.system_role,
      flag_criteria: defaults.flag_criteria,
      analysis_priorities: defaults.analysis_priorities,
      additional_instructions: defaults.additional_instructions,
    });
    setShowResetConfirm(false);
  };

  const handleDiscard = () => {
    if (savedValues) {
      setFields({ ...savedValues });
    }
  };

  const handleFieldChange = (field, value) => {
    setFields((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.headerTitle}>Prompt Template Editor</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {hasChanges && (
            <span style={styles.unsavedBadge}>Unsaved Changes</span>
          )}
          <button
            onClick={() => setShowGuide(!showGuide)}
            style={showGuide ? styles.btnActive : styles.btnOutline}
          >
            {showGuide ? 'Hide Guide' : 'Guide'}
          </button>
        </div>
      </div>

      {/* Tab Switcher */}
      <div style={styles.tabBar}>
        <button
          onClick={() => setActiveTab('daily')}
          style={activeTab === 'daily' ? styles.tabActive : styles.tab}
        >
          Daily Report
        </button>
        <button
          onClick={() => setActiveTab('weekly')}
          style={activeTab === 'weekly' ? styles.tabActive : styles.tab}
        >
          Weekly Report
        </button>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          Loading template...
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1, padding: '16px' }}>
            {/* System Role */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>System Role</label>
              <p style={styles.helpText}>Defines the AI's persona and expertise</p>
              <textarea
                style={styles.textarea}
                rows={5}
                value={fields.system_role}
                onChange={(e) => handleFieldChange('system_role', e.target.value)}
              />
            </div>

            {/* Flag Criteria */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Flag Criteria</label>
              <p style={styles.helpText}>Rules for what triggers critical/warning/info flags</p>
              <textarea
                style={styles.textarea}
                rows={8}
                value={fields.flag_criteria}
                onChange={(e) => handleFieldChange('flag_criteria', e.target.value)}
              />
            </div>

            {/* Analysis Priorities (weekly only) */}
            {activeTab === 'weekly' && (
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Analysis Priorities</label>
                <p style={styles.helpText}>What patterns to focus on in weekly analysis</p>
                <textarea
                  style={styles.textarea}
                  rows={6}
                  value={fields.analysis_priorities}
                  onChange={(e) => handleFieldChange('analysis_priorities', e.target.value)}
                />
              </div>
            )}

            {/* Additional Instructions */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Additional Instructions</label>
              <p style={styles.helpText}>Extra context or rules for this deployment</p>
              <textarea
                style={styles.textarea}
                rows={3}
                value={fields.additional_instructions}
                onChange={(e) => handleFieldChange('additional_instructions', e.target.value)}
              />
            </div>

            {/* Action Buttons */}
            <div style={styles.actionRow}>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                style={{
                  ...styles.btnPrimary,
                  opacity: saving || !hasChanges ? 0.5 : 1,
                }}
              >
                {saving ? 'Saving...' : 'Save Template'}
              </button>
              <button
                onClick={() => setShowResetConfirm(true)}
                style={styles.btnOutline}
              >
                Reset to Defaults
              </button>
              <button
                onClick={handleDiscard}
                disabled={!hasChanges}
                style={{ ...styles.btnOutline, opacity: !hasChanges ? 0.5 : 1 }}
              >
                Discard Changes
              </button>
            </div>
          </div>

          {/* Guide Panel */}
          {showGuide && (
            <div style={styles.guidePanel}>
              <h4 style={{ margin: '0 0 12px', color: '#333' }}>Reference Guide</h4>

              <div style={styles.guideSection}>
                <h5 style={styles.guideLabel}>Units</h5>
                {UNIT_REFERENCE.map((u) => (
                  <div key={u.table} style={styles.guideItem}>
                    <strong>{u.label}</strong>
                    <span style={{ color: '#888', fontSize: '12px' }}>({u.type})</span>
                  </div>
                ))}
              </div>

              <div style={styles.guideSection}>
                <h5 style={styles.guideLabel}>Medications</h5>
                {MED_REFERENCE.map((m) => (
                  <div key={m} style={styles.guideItem}>{m}</div>
                ))}
              </div>

              <div style={styles.guideSection}>
                <h5 style={styles.guideLabel}>Severity Levels</h5>
                <div style={styles.guideItem}><span style={{ color: '#d32f2f' }}>critical</span></div>
                <div style={styles.guideItem}><span style={{ color: '#f57c00' }}>warning</span></div>
                <div style={styles.guideItem}><span style={{ color: '#1976d2' }}>info</span></div>
              </div>

              <div style={styles.guideSection}>
                <h5 style={styles.guideLabel}>Status Values</h5>
                <div style={styles.guideItem}><span style={{ color: '#388e3c' }}>compliant</span></div>
                <div style={styles.guideItem}><span style={{ color: '#f57c00' }}>needs_attention</span></div>
                <div style={styles.guideItem}><span style={{ color: '#d32f2f' }}>non_compliant</span></div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div style={styles.modalOverlay} onClick={() => setShowResetConfirm(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', color: '#333' }}>Reset to Defaults?</h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              This will replace all fields with the default template values. Unsaved changes will be lost.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowResetConfirm(false)} style={styles.btnOutline}>Cancel</button>
              <button onClick={handleReset} style={{ ...styles.btnPrimary, background: '#d32f2f' }}>Reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    marginBottom: '24px',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '20px',
    borderBottom: '1px solid #eee',
  },
  headerTitle: {
    margin: 0,
    fontSize: '20px',
    color: '#333',
    fontWeight: '600',
  },
  tabBar: {
    display: 'flex',
    borderBottom: '1px solid #eee',
  },
  tab: {
    padding: '12px 24px',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#666',
  },
  tabActive: {
    padding: '12px 24px',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid #0E3293',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    color: '#0E3293',
  },
  fieldGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontWeight: '600',
    color: '#333',
    marginBottom: '4px',
    fontSize: '14px',
  },
  helpText: {
    color: '#888',
    fontSize: '12px',
    margin: '0 0 8px',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: 'monospace',
    resize: 'vertical',
    lineHeight: '1.5',
    boxSizing: 'border-box',
  },
  actionRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    paddingTop: '8px',
  },
  unsavedBadge: {
    background: '#fff3e0',
    color: '#e65100',
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  guidePanel: {
    width: '260px',
    padding: '16px',
    background: '#f8f9fa',
    borderLeft: '1px solid #eee',
    overflowY: 'auto',
    maxHeight: '600px',
    flexShrink: 0,
  },
  guideSection: {
    marginBottom: '16px',
  },
  guideLabel: {
    margin: '0 0 6px',
    color: '#555',
    fontSize: '13px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  guideItem: {
    padding: '2px 0',
    fontSize: '13px',
    color: '#444',
  },
  btnPrimary: {
    padding: '8px 16px',
    background: '#0E3293',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
  },
  btnOutline: {
    padding: '8px 16px',
    background: '#fff',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
  },
  btnActive: {
    padding: '8px 16px',
    background: '#e3f2fd',
    color: '#0E3293',
    border: '1px solid #0E3293',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    width: '90%',
    maxWidth: '400px',
  },
};

export default PromptTemplateEditor;
